import { Suspense, lazy, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/Layout";
import { DataTable, type DataTableColumn } from "@/components/table";
import { Button, ConnectionState, Dash, DecimalField, ErrorNote, Field, Hint, inputClass, useCanWrite } from "@/components/ui";
import { ApiError, api, errorMessage } from "@/lib/api";
import { dateTime, lots as fmtLots, money, points, sentence, signClass } from "@/lib/format";
import { keys, useDebounced } from "@/lib/queries";
import { decimalInput, decimalOrZero, parseDecimal } from "@/lib/units";
import type { SingleCloseReason, SingleTrade, SingleTradesPage } from "@/lib/types";

/*
 * OPERAZIONE SINGOLA: un conto solo, fuori dalle coppie.
 *
 * Quattro pezzi, dall'alto: il grafico con SL e TP trascinabili, il modulo di
 * apertura, le singole aperte (Chiudi e modifica dei livelli), la Cronistoria.
 * SL e TP si scrivono in DENARO, nella valuta del conto; punti e prezzi li
 * calcola il backend, e qui si mostrano accanto ai campi — mai il numero
 * digitato: quello ricalcolato dai punti e dai lotti che vanno a mercato.
 *
 * Se il conto sta in una Fase non chiusa lo si dice in cima, e l'apertura
 * chiede una conferma: la singola entra nel DD di quella Fase.
 */

/** Il grafico si carica solo qui dentro: ~45 kB gzip di libreria. */
const LevelsChart = lazy(() => import("@/components/chart").then((m) => ({ default: m.LevelsChart })));

const CLOSE_REASON: Record<SingleCloseReason, string> = {
  MANUAL_CLOSE: "Chiusura manuale",
  SL_HIT: "Stop loss",
  TP_HIT: "Take profit",
  CLOSED_BY_SERVER: "Chiusa dal server, motivo non deducibile",
};

const sideLabel = (s: "BUY" | "SELL") => (s === "BUY" ? "Buy" : "Sell");
const roleLabel = (r: string) => (r === "PROP" ? "prop" : r === "BROKER" ? "broker" : "senza ruolo");

/** Il passo delle candele, in minuti. */
const TIMEFRAMES = [1, 5] as const;
type Timeframe = (typeof TIMEFRAMES)[number];
/** 24 ore di storico, come sulla coppia. */
const barsFor = (tf: Timeframe) => Math.ceil((24 * 60) / tf);

/** Oggi a Roma, «AAAA-MM-GG»: lo stesso giorno su cui filtra il backend. */
const romeToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const shiftDay = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** Il conto resta attivo mentre la pagina e' aperta, e si ricollega se scollegato. */
function useActivityHeartbeat(accountId: string, enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    let viva = true;
    const battito = () =>
      api.singleTrades.activity(accountId).then(
        (r) => {
          if (viva && (r.reconnected || (r.suspended && !r.connected))) void qc.invalidateQueries({ queryKey: keys.singleTrades });
        },
        () => undefined,
      );
    void battito();
    const timer = setInterval(() => void battito(), 60_000);
    return () => {
      viva = false;
      clearInterval(timer);
    };
  }, [accountId, enabled, qc]);
}

export function SingleTradePage() {
  const { id = "" } = useParams();
  const canWrite = useCanWrite();
  useActivityHeartbeat(id, canWrite);
  const page = useQuery({
    queryKey: [...keys.singleTrades, "page", id],
    queryFn: () => api.singleTrades.page(id),
    // Con singole aperte il P&L corrente si rinfresca da solo.
    refetchInterval: (q) => ((q.state.data?.open.length ?? 0) > 0 ? 5_000 : false),
  });

  if (page.isLoading) return <p className="px-6 py-5 text-sm text-muted">Caricamento del conto…</p>;
  if (page.error || !page.data) {
    return (
      <div className="px-6 py-5">
        <ErrorNote>{page.error ? errorMessage(page.error) : "Conto non trovato"}</ErrorNote>
      </div>
    );
  }
  return <SingleTradeView p={page.data} canWrite={canWrite} />;
}

function SingleTradeView({ p, canWrite }: { p: SingleTradesPage; canWrite: boolean }) {
  const a = p.account;
  const tone = a.role === "PROP" ? "prop" : "broker";
  /** Quale singola aperta mostra il grafico; `null` = la nuova operazione del modulo. */
  const [focusId, setFocusId] = useState<string | null>(p.open[0]?.id ?? null);
  const focus = p.open.find((t) => t.id === focusId) ?? null;

  // Il modulo: vive qui perche' il grafico lo legge (linee del piano) e lo scrive (trascinamento).
  const [side, setSide] = useState<"BUY" | "SELL">("BUY");
  const [symbol, setSymbol] = useState("");
  const [lotsText, setLotsText] = useState("");
  const [slText, setSlText] = useState("");
  const [tpText, setTpText] = useState("");

  // La modifica della singola a fuoco: i suoi livelli in denaro, come li ha registrati.
  const [modSl, setModSl] = useState("");
  const [modTp, setModTp] = useState("");
  useEffect(() => {
    setModSl(focus?.slMoney ? String(Number(focus.slMoney)) : "");
    setModTp(focus?.tpMoney ? String(Number(focus.tpMoney)) : "");
  }, [focus?.id, focus?.slMoney, focus?.tpMoney]);

  const entry = symbol ? p.symbols.find((x) => x.canonicalSymbol === symbol) ?? null : null;
  const op = { lots: decimalInput(lotsText), sl: decimalInput(slText), tp: decimalInput(tpText) };
  // Un campo scritto ma non numerico non e' un campo vuoto: lo dice il campo, e il modulo non apre.
  const fieldError = [lotsText, slText, tpText].some((t) => parseDecimal(t).kind === "error");
  const complete = !!entry && !entry.blocker && !!op.lots && !!op.sl && !fieldError;
  const opKey = JSON.stringify({ side, symbol, ...op });
  const debounced = useDebounced(complete ? opKey : null, 300);
  const preview = useQuery({
    queryKey: [...keys.singleTrades, "preview", a.id, debounced],
    queryFn: () => {
      const o = JSON.parse(debounced!) as { side: "BUY" | "SELL"; symbol: string; lots: string; sl: string; tp: string | null };
      return api.singleTrades.preview(a.id, { canonicalSymbol: o.symbol, side: o.side, lots: o.lots, slMoney: o.sl, ...(o.tp ? { tpMoney: o.tp } : {}) });
    },
    // Conto scollegato: nessuna anteprima (il backend non collega), la pagina dice «premi Connetti».
    enabled: !!debounced && a.isConnected,
    placeholderData: (prev) => prev,
  });
  const planIsCurrent = !!preview.data && preview.data.canonicalSymbol === symbol && debounced === opKey;

  // Il simbolo del grafico: quello della singola a fuoco, o quello scelto nel modulo.
  const chartSymbol = focus ? focus.canonicalSymbol : symbol;

  return (
    <div>
      <PageHeader
        title="Operazione singola"
        subtitle={`${a.label} · conto ${roleLabel(a.role)} di ${a.client.name} · ${a.server.displayName ?? a.server.name}`}
        actions={
          <Link to={`/clienti/${a.client.id}`} className="text-sm text-muted hover:text-ink">
            Scheda cliente
          </Link>
        }
      />
      <div className="space-y-5 px-6 py-5">
        <div className={`card flex flex-wrap items-center gap-x-6 gap-y-2 p-3 text-sm pair-side-${tone}`}>
          <span className={`font-medium ${tone === "prop" ? "text-prop" : "text-broker"}`}>{a.label}</span>
          <span className="machine-id text-muted">{a.mt5Login}</span>
          <ConnectionState connected={a.isConnected} error={a.lastConnectError} suspendedReason={a.connectionSuspendedReason} compact />
          <span className="text-muted">
            Saldo <span className="tabular-nums text-ink">{a.lastBalance === null ? "—" : money(a.lastBalance, a.currency)}</span>
          </span>
          <span className="text-muted">
            Equity <span className="tabular-nums text-ink">{a.lastEquity === null ? "—" : money(a.lastEquity, a.currency)}</span>
          </span>
        </div>

        {p.session && (
          <div role="status" className="rounded-card border border-line bg-raised/50 px-4 py-3 text-sm text-ink">
            Il conto è nella fase{" "}
            <Link to={`/fasi/${p.session.id}`} className="font-medium hover:underline">
              {p.session.label}
            </Link>
            : le operazioni singole entrano nel suo DD, nel DD giornaliero, nel target e nel suo storico. L'apertura chiede una conferma.
          </div>
        )}

        {a.role === "NONE" ? (
          <ErrorNote>Il conto non ha un ruolo: assegnagli prop o broker dalla scheda cliente per operarci.</ErrorNote>
        ) : (
          <>
            <SingleChart
              p={p}
              symbol={chartSymbol}
              focus={focus}
              canWrite={canWrite}
              modSl={modSl}
              modTp={modTp}
              onDrag={(level, v) => (level === "SL" ? setModSl(String(v)) : setModTp(String(v)))}
              planned={!focus && complete && planIsCurrent ? (preview.data?.plan ?? null) : null}
              plannedNote={!focus && complete && preview.data && !preview.data.plan ? (preview.data.blocker?.message ?? null) : null}
              onPlannedDrop={async (level, price) => {
                const r = await api.singleTrades.preview(a.id, {
                  canonicalSymbol: symbol,
                  side,
                  lots: op.lots!,
                  ...(level === "SL" ? { slPrice: price, ...(op.tp ? { tpMoney: op.tp } : {}) } : { slMoney: op.sl!, tpPrice: price }),
                });
                const m = level === "SL" ? r.fromPrice?.sl : r.fromPrice?.tp;
                if (m == null) throw new Error(r.blocker?.message ?? "l'anteprima non ha restituito l'importo");
                (level === "SL" ? setSlText : setTpText)(m.toFixed(2));
              }}
            />

            {p.open.length > 0 && (
              <div role="radiogroup" aria-label="Che cosa mostra il grafico" className="inline-flex flex-wrap overflow-hidden rounded border border-line text-xs">
                {p.open.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="radio"
                    aria-checked={focusId === t.id}
                    onClick={() => setFocusId(t.id)}
                    className={`px-3 py-1 ${focusId === t.id ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
                  >
                    {t.canonicalSymbol} {sideLabel(t.side)} {fmtLots(t.lots)}
                  </button>
                ))}
                <button
                  type="button"
                  role="radio"
                  aria-checked={focusId === null}
                  onClick={() => setFocusId(null)}
                  className={`px-3 py-1 ${focusId === null ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
                >
                  Nuova operazione
                </button>
              </div>
            )}

            {canWrite && (
              <OpenForm
                p={p}
                side={side}
                setSide={setSide}
                symbol={symbol}
                setSymbol={(v) => {
                  setSymbol(v);
                  setFocusId(null);
                }}
                lotsText={lotsText}
                setLotsText={setLotsText}
                slText={slText}
                setSlText={setSlText}
                tpText={tpText}
                setTpText={setTpText}
                preview={planIsCurrent ? (preview.data ?? null) : null}
                previewError={preview.error}
                stale={preview.isFetching || (complete && debounced !== opKey)}
                complete={complete}
                fieldError={fieldError}
                onOpened={(id) => setFocusId(id)}
              />
            )}

            <OpenSingles p={p} canWrite={canWrite} focusId={focusId} setFocusId={setFocusId} modSl={modSl} setModSl={setModSl} modTp={modTp} setModTp={setModTp} />
          </>
        )}

        <SinglesHistory accountId={a.id} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grafico
// ---------------------------------------------------------------------------

function SingleChart({
  p,
  symbol,
  focus,
  canWrite,
  modSl,
  modTp,
  onDrag,
  planned,
  plannedNote,
  onPlannedDrop,
}: {
  p: SingleTradesPage;
  symbol: string;
  focus: SingleTrade | null;
  canWrite: boolean;
  modSl: string;
  modTp: string;
  onDrag: (level: "SL" | "TP", money: number) => void;
  planned: { side: "BUY" | "SELL"; entryPrice: number | null; slPrice: number | null; tpPrice: number | null; money: { sl: number; tp: number } } | null;
  plannedNote: string | null;
  onPlannedDrop: (level: "SL" | "TP", price: number) => Promise<void>;
}) {
  const a = p.account;
  const [timeframe, setTimeframe] = useState<Timeframe>(5);
  const candles = useQuery({
    queryKey: [...keys.singleTrades, "candles", a.id, symbol, timeframe],
    queryFn: () => api.singleTrades.candles(a.id, { canonicalSymbol: symbol, timeframe, bars: barsFor(timeframe) }),
    enabled: a.isConnected && !!symbol,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  if (!symbol) {
    return <p className="rounded border border-dashed border-line p-6 text-center text-sm text-muted">Scegli il simbolo nel modulo qui sotto per vedere il grafico.</p>;
  }
  if (!a.isConnected) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded border border-dashed border-line p-6 text-center">
        <p className="text-sm text-muted">Connetti il conto per vedere il grafico</p>
        {canWrite && (
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.accounts.connect(a.id);
              } catch (e) {
                setError(errorMessage(e));
              } finally {
                await qc.invalidateQueries({ queryKey: keys.singleTrades });
                setBusy(false);
              }
            }}
          >
            {busy ? "Connessione…" : "Connetti"}
          </Button>
        )}
        {error && (
          <p role="alert" className="text-xs text-loss">
            {error}
          </p>
        )}
      </div>
    );
  }
  const chart = candles.data;
  const leg =
    focus && focus.openPrice
      ? { side: focus.side, openPrice: Number(focus.openPrice), slPrice: focus.slPrice === null ? null : Number(focus.slPrice), tpPrice: focus.tpPrice === null ? null : Number(focus.tpPrice), lots: Number(focus.lots) }
      : null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <div role="radiogroup" aria-label="Passo delle candele" className="inline-flex overflow-hidden rounded border border-line text-xs">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf}
              type="button"
              role="radio"
              aria-checked={timeframe === tf}
              onClick={() => setTimeframe(tf)}
              className={`px-3 py-1 ${timeframe === tf ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
            >
              M{tf}
            </button>
          ))}
        </div>
        <Button size="sm" variant="ghost" disabled={candles.isFetching} onClick={() => void candles.refetch()}>
          Aggiorna
        </Button>
        <span className="text-xs text-faint">
          {symbol}
          {chart ? ` · ${chart.symbol} su ${chart.account.server}` : ""}
          {chart && chart.barsReturned < chart.barsRequested ? ` · ${chart.barsReturned} candele su ${chart.barsRequested} chieste` : ""}
        </span>
      </div>
      {candles.isLoading && <p className="text-xs text-faint">Caricamento del grafico…</p>}
      {candles.isError && <p className="text-xs text-faint">Grafico non disponibile: {errorMessage(candles.error)}. I livelli si scrivono lo stesso nei campi.</p>}
      {chart && (!focus || leg) && (
        <Suspense fallback={<p className="text-xs text-faint">Caricamento del grafico…</p>}>
          <LevelsChart
            candles={chart}
            leg={leg}
            point={chart.point}
            valuePerPoint={chart.valuePerPoint.value}
            currency={a.currency}
            slMoney={focus ? modSl : ""}
            tpMoney={focus ? modTp : ""}
            slEditing={!!focus && decimalOrZero(modSl) !== Number(focus.slMoney ?? 0)}
            tpEditing={!!focus && decimalOrZero(modTp) !== Number(focus.tpMoney ?? 0)}
            draggable={canWrite}
            onDrag={onDrag}
            planned={
              planned
                ? { side: planned.side, entryPrice: planned.entryPrice, slPrice: planned.slPrice, tpPrice: planned.tpPrice, slMoney: planned.money.sl, tpMoney: planned.money.tp }
                : null
            }
            plannedNote={plannedNote}
            onPlannedDrop={onPlannedDrop}
          />
        </Suspense>
      )}
      {chart && (
        <p className="text-xs text-faint">
          valore punto {money(chart.valuePerPoint.value, a.currency)} · {chart.valuePerPoint.source === "LIVE" ? "letto adesso" : "salvato in mappatura (stima)"}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modulo di apertura
// ---------------------------------------------------------------------------

function OpenForm({
  p,
  side,
  setSide,
  symbol,
  setSymbol,
  lotsText,
  setLotsText,
  slText,
  setSlText,
  tpText,
  setTpText,
  preview,
  previewError,
  stale,
  complete,
  fieldError,
  onOpened,
}: {
  p: SingleTradesPage;
  side: "BUY" | "SELL";
  setSide: (s: "BUY" | "SELL") => void;
  symbol: string;
  setSymbol: (s: string) => void;
  lotsText: string;
  setLotsText: (s: string) => void;
  slText: string;
  setSlText: (s: string) => void;
  tpText: string;
  setTpText: (s: string) => void;
  preview: Awaited<ReturnType<typeof api.singleTrades.preview>> | null;
  previewError: unknown;
  stale: boolean;
  complete: boolean;
  fieldError: boolean;
  onOpened: (id: string) => void;
}) {
  const a = p.account;
  const cur = a.currency;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Secondo clic: il conto sta in una Fase, e la singola ne muovera' il DD. */
  const [confirming, setConfirming] = useState(false);
  const entry = symbol ? p.symbols.find((x) => x.canonicalSymbol === symbol) ?? null : null;
  const plan = preview?.plan ?? null;
  // Anti-hedging: si vede prima di premere, a ogni cambio di simbolo o direzione. Solo database.
  const hedging = useQuery({
    queryKey: [...keys.singleTrades, "hedging", a.id, symbol, side, p.open.length],
    queryFn: () => api.singleTrades.hedgingCheck(a.id, { canonicalSymbol: symbol, side }),
    enabled: !!symbol && a.role === "PROP",
  });

  const blocker = !p.account.isConnected
    ? `Conto ${roleLabel(p.account.role)} scollegato: premi Connetti`
    : !symbol
    ? "Scegli il simbolo"
    : entry?.blocker
      ? entry.blocker.message
      : hedging.data?.blocker
        ? hedging.data.blocker.message
      : fieldError
        ? "Correggi i campi segnati: accettano solo numeri, con la virgola o col punto"
        : !complete
          ? "Scrivi lotti e stop loss: il take profit vuoto vuol dire nessun take profit"
          : previewError
            ? errorMessage(previewError)
            : (preview?.blocker?.message ?? null);
  const canOpen = complete && !!plan && !blocker && !stale;

  const apri = async () => {
    if (p.session && !confirming) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api.singleTrades.open(a.id, {
        canonicalSymbol: symbol,
        side,
        lots: Number(decimalInput(lotsText)),
        slMoney: Number(decimalInput(slText)),
        ...(decimalInput(tpText) ? { tpMoney: Number(decimalInput(tpText)) } : {}),
        ...(p.session ? { confirmSessionId: p.session.id } : {}),
      });
      setNotice(`Aperta: ${r.trade.canonicalSymbol} ${sideLabel(r.trade.side)} ${fmtLots(r.trade.lots)} lotti, ticket ${r.trade.mt5Ticket}.`);
      onOpened(r.trade.id);
      for (const w of r.warnings) setNotice((n) => `${n ?? ""} ${w}`.trim());
    } catch (e) {
      setError(errorMessage(e) + (e instanceof ApiError && e.code === "SESSION_CONFIRM_REQUIRED" ? " (la fase del conto e' cambiata: riguarda l'avviso sopra)" : ""));
    } finally {
      setConfirming(false);
      setBusy(false);
    }
  };

  const campo = (label: string, value: string, set: (v: string) => void, opts: { suffix?: string; hint?: string; after?: string | null } = {}) => (
    <DecimalField
      label={label}
      value={value}
      onChange={(v) => {
        set(v);
        setConfirming(false);
      }}
      suffix={opts.suffix}
      hint={opts.hint}
      after={opts.after ? <p className={`mt-1 text-xs tabular-nums text-muted ${stale ? "opacity-60" : ""}`}>{opts.after}</p> : null}
    />
  );

  return (
    <section className="space-y-3 rounded-card border border-line bg-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-ink">Nuova operazione singola</h2>
        <div role="radiogroup" aria-label="Direzione" className="inline-flex overflow-hidden rounded border border-line">
          {(["BUY", "SELL"] as const).map((d) => (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={side === d}
              onClick={() => {
                setSide(d);
                setConfirming(false);
              }}
              className={`px-4 py-1.5 text-sm ${side === d ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
            >
              {sideLabel(d)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field label="Simbolo">
          {(id) => (
            <select
              id={id}
              value={symbol}
              onChange={(e) => {
                setSymbol(e.target.value);
                setConfirming(false);
              }}
              className={inputClass}
            >
              <option value="" disabled>
                Scegli il simbolo
              </option>
              {p.symbols.map((x) => (
                <option key={x.canonicalSymbol} value={x.canonicalSymbol} disabled={!!x.blocker}>
                  {x.canonicalSymbol}
                  {x.brokerSymbol !== x.canonicalSymbol ? ` (${x.brokerSymbol})` : ""}
                  {x.blocker ? " · non utilizzabile" : ""}
                </option>
              ))}
            </select>
          )}
        </Field>
        {campo("Lotti", lotsText, setLotsText, { after: plan ? `a mercato ${fmtLots(plan.lots)}` : null })}
        {campo("Stop loss", slText, setSlText, {
          suffix: cur,
          after: plan ? `${plan.slPrice ?? "—"} · ${points(plan.slPoints)} · ${money(plan.money.sl, cur)}` : null,
        })}
        {campo("Take profit", tpText, setTpText, {
          suffix: cur,
          hint: "Vuoto = nessun take profit.",
          after: plan ? (plan.tpPoints === 0 ? "nessun take profit" : `${plan.tpPrice ?? "—"} · ${points(plan.tpPoints)} · ${money(plan.money.tp, cur)}`) : null,
        })}
      </div>
      {p.symbols.length === 0 && <p className="text-xs text-warn">Nessun simbolo mappato sul server di questo conto: aggiungi la mappatura dalla pagina Server.</p>}
      {p.symbols.flatMap((x) => (x.blocker ? [<p key={x.canonicalSymbol} className="text-xs text-warn">{x.canonicalSymbol}: {x.blocker.message}</p>] : []))}
      {preview && (
        <p className="text-xs text-faint">
          Prezzi dall'ingresso stimato {preview.plan?.entryPrice ?? "—"} ({side === "BUY" ? "ask" : "bid"}); valore punto {money(preview.valuePerPoint.value, cur)}
          {preview.valuePerPoint.source === "STORED" ? " salvato in mappatura: all'apertura si rilegge dal conto" : " letto adesso"}.
          <Hint>Gli importi accanto ai campi sono ricalcolati dai punti e dai lotti che vanno a mercato, per difetto: non il numero scritto.</Hint>
        </p>
      )}

      {confirming && p.session && (
        <div role="status" className="rounded border border-warn/40 bg-warn/5 px-3 py-2 text-sm text-ink">
          Il conto è nella fase «{p.session.label}»: questa operazione entra nel suo DD, nel DD giornaliero, nel target e nel suo storico.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant={confirming ? "danger" : "primary"} disabled={busy || !canOpen} onClick={() => void apri()}>
          {busy ? "Apertura…" : confirming ? "Conferma: apri nella fase" : "Apri operazione"}
        </Button>
        {confirming && (
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Annulla
          </Button>
        )}
        {blocker && (
          <span role="status" className="text-sm text-warn">
            {sentence(blocker)}
          </span>
        )}
      </div>
      <ErrorNote>{error}</ErrorNote>
      {notice && <p className="text-sm text-muted">{notice}</p>}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Singole aperte
// ---------------------------------------------------------------------------

function OpenSingles({
  p,
  canWrite,
  focusId,
  setFocusId,
  modSl,
  setModSl,
  modTp,
  setModTp,
}: {
  p: SingleTradesPage;
  canWrite: boolean;
  focusId: string | null;
  setFocusId: (id: string | null) => void;
  modSl: string;
  setModSl: (v: string) => void;
  modTp: string;
  setModTp: (v: string) => void;
}) {
  const cur = p.account.currency;
  if (!p.open.length) {
    return <p className="text-sm text-muted">Nessuna operazione singola aperta su questo conto. Quelle che apri compaiono qui, con Chiudi e la modifica dei livelli.</p>;
  }
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-medium text-ink">Operazioni singole aperte</h2>
      {p.open.map((t) => {
        const pnl = t.floating ? t.floating.profit + t.floating.swap + t.floating.commission : null;
        return (
          <div key={t.id} className={`card space-y-2 p-3 text-sm ${focusId === t.id ? "ring-1 ring-line" : ""}`}>
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <button type="button" className="font-medium text-ink hover:underline" onClick={() => setFocusId(t.id)}>
                {t.canonicalSymbol} {sideLabel(t.side)} {fmtLots(t.lots)} lotti
              </button>
              {t.state === "PENDING" ? (
                <span className="text-warn">In apertura</span>
              ) : (
                <>
                  <span className="text-muted">
                    ingresso <span className="tabular-nums text-ink">{t.openPrice ?? "—"}</span>
                  </span>
                  <span className="text-muted">
                    SL <span className="tabular-nums text-ink">{t.slMoney ? money(t.slMoney, cur) : "—"}</span> ({t.slPrice ?? "—"})
                  </span>
                  <span className="text-muted">
                    TP <span className="tabular-nums text-ink">{t.tpPoints === 0 ? "nessuno" : t.tpMoney ? money(t.tpMoney, cur) : "—"}</span>
                    {t.tpPoints !== 0 && ` (${t.tpPrice ?? "—"})`}
                  </span>
                  <span className="text-muted">
                    P&L{" "}
                    {pnl === null ? (
                      <span className="text-faint" title="Il conto non è connesso: il P&L corrente non si legge">non letto</span>
                    ) : (
                      <span className={`tabular-nums ${signClass(pnl)}`}>{money(pnl, cur)}</span>
                    )}
                  </span>
                </>
              )}
              <span className="machine-id text-xs text-faint">{t.mt5Ticket ?? "senza ticket"}</span>
              {t.sessionId && (
                <Link to={`/fasi/${t.sessionId}`} className="text-xs text-muted hover:underline">
                  nella fase
                </Link>
              )}
            </div>
            {canWrite && t.state === "OPEN" && (
              <div className="flex flex-wrap items-end gap-3">
                {focusId === t.id ? (
                  <LevelsEditor trade={t} currency={cur} modSl={modSl} setModSl={setModSl} modTp={modTp} setModTp={setModTp} />
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setFocusId(t.id)}>
                    Modifica livelli
                  </Button>
                )}
                <CloseSingleButton trade={t} />
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

function LevelsEditor({
  trade,
  currency,
  modSl,
  setModSl,
  modTp,
  setModTp,
}: {
  trade: SingleTrade;
  currency: string;
  modSl: string;
  setModSl: (v: string) => void;
  modTp: string;
  setModTp: (v: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [esito, setEsito] = useState<string | null>(null);
  const toccato = decimalOrZero(modSl) !== Number(trade.slMoney ?? 0) || decimalOrZero(modTp) !== Number(trade.tpMoney ?? 0);
  const sl = decimalInput(modSl);
  const tp = modTp.trim() === "" ? "0" : decimalInput(modTp);
  const applica = async () => {
    setBusy(true);
    setError(null);
    setEsito(null);
    try {
      const r = await api.singleTrades.modifyLevels(trade.id, { slMoney: Number(sl), tpMoney: Number(tp) });
      setEsito(`Livelli a mercato: SL ${money(r.money.sl, currency)}${r.money.tp ? `, TP ${money(r.money.tp, currency)}` : ", nessun take profit"}.`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const campo = (label: string, value: string, set: (v: string) => void) => (
    <DecimalField label={label} value={value} onChange={set} suffix={currency} className="w-36" />
  );
  return (
    <>
      {campo("Stop loss", modSl, setModSl)}
      {campo("Take profit", modTp, setModTp)}
      <Button size="sm" variant="primary" disabled={busy || !toccato || !sl || tp === null} onClick={() => void applica()}>
        {busy ? "Modifica…" : "Applica"}
      </Button>
      <span className="text-xs text-faint">Dal prezzo di apertura; trascina le linee sul grafico o scrivi l'importo.</span>
      {esito && <span className="text-xs text-muted">{esito}</span>}
      {error && (
        <span role="alert" className="text-xs text-loss">
          {error}
        </span>
      )}
    </>
  );
}

/** Chiude per intero, con conferma. */
function CloseSingleButton({ trade }: { trade: SingleTrade }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chiudi = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.singleTrades.close(trade.id);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {confirm ? (
        <>
          <Button size="sm" variant="danger" disabled={busy} onClick={() => void chiudi()}>
            {busy ? "Chiusura…" : "Conferma: chiudi tutto"}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirm(false)}>
            Annulla
          </Button>
        </>
      ) : (
        <Button size="sm" onClick={() => setConfirm(true)}>
          Chiudi
        </Button>
      )}
      {error && (
        <span role="alert" className="text-xs text-loss">
          {error}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Cronistoria
// ---------------------------------------------------------------------------

function SinglesHistory({ accountId }: { accountId: string }) {
  const today = romeToday();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(today);
  const q = useQuery({
    queryKey: [...keys.singleTrades, "closed", accountId, from, to],
    queryFn: () => api.singleTrades.closed(accountId, { from: from || undefined, to: to || undefined }),
    placeholderData: (prev) => prev,
  });
  const shortcuts = [
    { label: "Oggi", from: today },
    { label: "7 giorni", from: shiftDay(today, -6) },
    { label: "30 giorni", from: shiftDay(today, -29) },
    { label: "Dall'inizio", from: "" },
  ];
  const t = q.data?.totals;
  const pnl = (x: SingleTrade) => (x.pnlEffective === null ? null : Number(x.pnlEffective));
  const columns: DataTableColumn<SingleTrade>[] = [
    { key: "symbol", header: "Simbolo", cell: (x) => x.canonicalSymbol, sortBy: (x) => x.canonicalSymbol },
    { key: "side", header: "Direzione", cell: (x) => sideLabel(x.side), sortBy: (x) => x.side },
    { key: "lots", header: "Lotti", align: "right", cell: (x) => <span className="tabular-nums">{fmtLots(x.lots)}</span>, sortBy: (x) => Number(x.lots) },
    {
      key: "pnl",
      header: "P&L",
      align: "right",
      cell: (x) => {
        const v = pnl(x);
        if (!x.mt5Ticket) return <Dash title="Mai andata a mercato" />;
        return v === null ? <span className="text-xs text-loss" title="Lo storico non l'ha ancora data: si ritenta">non letto</span> : <span className={`tabular-nums ${signClass(v)}`}>{money(v, x.currency)}</span>;
      },
      sortBy: pnl,
    },
    {
      key: "reason",
      header: "Causa di chiusura",
      cell: (x) =>
        x.state === "FAILED_OPEN" ? (
          <span className="font-medium text-loss">Apertura non riuscita, arrivata dopo e chiusa</span>
        ) : x.closeReason ? (
          <span>
            {CLOSE_REASON[x.closeReason]}
            <span className="text-xs text-faint">{x.closeReasonSource === "DEDUCED" ? " · dedotta dal prezzo" : x.closeReasonSource === "OPERATOR" ? " · dal gestionale" : ""}</span>
          </span>
        ) : (
          <span className="text-faint">Da determinare</span>
        ),
      sortBy: (x) => x.closeReason,
    },
    { key: "phase", header: "Fase", cell: (x) => (x.sessionId ? <Link to={`/fasi/${x.sessionId}`} className="hover:underline">sì</Link> : <Dash />) },
    { key: "opened", header: "Aperta", cell: (x) => <span className="text-muted">{dateTime(x.openedAt ?? x.requestedAt)}</span>, sortBy: (x) => x.openedAt ?? x.requestedAt },
    { key: "closed", header: "Chiusa", cell: (x) => <span className="text-muted">{dateTime(x.closedAt)}</span>, sortBy: (x) => x.closedAt },
    { key: "ticket", header: "Ticket", cell: (x) => <span className="machine-id text-xs">{x.mt5Ticket ?? "—"}</span> },
  ];
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium text-ink">Cronistoria</h2>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Dal">{(id) => <input id={id} type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={`${inputClass} py-1.5`} />}</Field>
        <Field label="Al">{(id) => <input id={id} type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={`${inputClass} py-1.5`} />}</Field>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Periodo">
          {shortcuts.map((sc) => {
            const active = from === sc.from && to === today;
            return (
              <Button
                key={sc.label}
                size="sm"
                variant={active ? "secondary" : "ghost"}
                aria-pressed={active}
                onClick={() => {
                  setFrom(sc.from);
                  setTo(today);
                }}
              >
                {sc.label}
              </Button>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-muted">Giorno di chiusura, ora italiana.</p>
      {q.isError && <ErrorNote>{errorMessage(q.error)}</ErrorNote>}
      {t && (
        <div className="card grid grid-cols-2 sm:grid-cols-3">
          <div className="p-3">
            <p className="text-xs font-medium text-muted">Operazioni</p>
            <p className="font-medium tabular-nums">{t.count}</p>
          </div>
          <div className="p-3">
            <p className="text-xs font-medium text-muted">P&L netto</p>
            {t.net === null ? (
              <>
                <p className="font-medium text-loss">Incompleto</p>
                <p className="text-xs text-muted">{t.missingPnl === 1 ? "1 operazione senza P&L" : `${t.missingPnl} operazioni senza P&L`}</p>
              </>
            ) : (
              <p className={`font-medium tabular-nums ${signClass(Number(t.net))}`}>{money(t.net, t.currency)}</p>
            )}
          </div>
          {t.adjustments.trades > 0 && (
            <div className="p-3">
              <p className="text-xs font-medium text-muted">Rettifiche incluse</p>
              <p className="tabular-nums">{money(t.adjustments.amount, t.currency)}</p>
            </div>
          )}
        </div>
      )}
      <div className="card">
        <DataTable
          bare
          columns={columns}
          rows={q.data?.trades ?? []}
          rowKey={(x) => x.id}
          initialSort={{ key: "closed", dir: "desc" }}
          rowClassName={(x) => (x.state === "FAILED_OPEN" ? "bg-loss/5" : "")}
          empty={
            q.isPending
              ? { title: "Caricamento…", hint: "Lettura delle operazioni singole chiuse nel periodo." }
              : from || to !== today
                ? { title: "Nessuna operazione singola chiusa in questo periodo", hint: "Allarga il periodo, o scegli «Dall'inizio»." }
                : { title: "Nessuna operazione singola chiusa oggi", hint: "Le singole chiuse compaiono qui, col P&L e la causa di chiusura." }
          }
        />
      </div>
    </section>
  );
}
